import { noteMoveColumns } from '@cerebro-diem/core';
import type { ContainerKind, Note } from '@cerebro-diem/core';
import { signedInUserId, supabase } from './supabase';

export type NoteUpdate = Partial<Pick<Note, 'title' | 'body' | 'project_id' | 'area_id' | 'resource_id'>>;

/**
 * Create a note titled `title` in one Project, Area or Resource, as the
 * signed-in user, and return its id. A note needs a title (the table refuses
 * a blank one), so a blank title writes nothing.
 */
export async function createNote(kind: ContainerKind, containerId: string, title: string): Promise<string> {
  const trimmed = title.trim();
  if (!trimmed) throw new Error('a note needs a title');
  const userId = await signedInUserId();
  const { data, error } = await supabase
    .from('notes')
    .insert({ user_id: userId, title: trimmed, ...noteMoveColumns({ kind, id: containerId }) })
    .select('id')
    .single();
  if (error) throw error;
  const id: unknown = data?.id;
  if (typeof id !== 'string') throw new Error('the server did not confirm the new note');
  return id;
}

/** Update one note. Throws when nothing was updated, so a silent no-op is never shown as saved. */
export async function updateNote(id: string, update: NoteUpdate): Promise<void> {
  const { data, error } = await supabase.from('notes').update(update).eq('id', id).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('note not found; it may have been deleted');
}
